import type { MigrationContextSummary } from "../api/migration";
import { isRegistration } from "./migrationFlow";

export const MIGRATION_CONTEXT_KEY = "migration_context";

/**
 * Tags the wizard's Auth0 sign-in on the callback URL itself: oidc-client-ts
 * sends `url_state` back as the suffix of the `state` query parameter
 * (`<id>;<url_state>`), so no storage flag can outlive an abandoned sign-in.
 */
export const MIGRATION_LINK_URL_STATE = "migration_link";

const URL_STATE_DELIMITER = ";";

/** True when the OIDC callback search string answers a migration link sign-in */
export function isMigrationLinkCallback(search: string): boolean {
  const state = new URLSearchParams(search).get("state");
  if (!state) {
    return false;
  }
  return (
    state.split(URL_STATE_DELIMITER).slice(1).join(URL_STATE_DELIMITER) ===
    MIGRATION_LINK_URL_STATE
  );
}

/**
 * OIDC nonce binding a link sign-in to its migration session: base64url(SHA-256(context id)).
 * The backend recomputes it from the session cookie (app/services/oidc_id_token.py).
 */
export async function migrationLinkNonce(contextId: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(contextId)
  );
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function readMigrationContext(): MigrationContextSummary | null {
  const storedContext = sessionStorage.getItem(MIGRATION_CONTEXT_KEY);
  if (!storedContext) {
    return null;
  }
  try {
    return JSON.parse(storedContext) as MigrationContextSummary;
  } catch (err) {
    console.error("Invalid migration context:", err);
    return null;
  }
}

export function writeMigrationContext(context: MigrationContextSummary): void {
  sessionStorage.setItem(MIGRATION_CONTEXT_KEY, JSON.stringify(context));
}

/** What the success page needs once the browser is back from Auth0. */
export interface MigrationConfirmSuccess {
  email?: string;
  passwordSet?: boolean;
  passwordEmailSent?: boolean;
  returnUrl?: string;
  sourceApplication?: string;
  /** Name shown for the application (metadata.app_display_name) */
  appDisplayName?: string;
  directoryUserId?: string;
  externalUserId?: string;
  linkedToExisting?: boolean;
  /** The application sent a person who has just registered with it */
  registration?: boolean;
}

/** The success payload: the outcome plus what the wizard context says about the application. */
export function buildMigrationConfirmSuccess(
  context: MigrationContextSummary,
  outcome: Omit<
    MigrationConfirmSuccess,
    | "returnUrl"
    | "sourceApplication"
    | "appDisplayName"
    | "externalUserId"
    | "registration"
  >
): MigrationConfirmSuccess {
  return {
    ...outcome,
    returnUrl: context.return_url,
    sourceApplication: context.source_application,
    appDisplayName: context.app_display_name ?? undefined,
    externalUserId: context.external_user_id ?? undefined,
    ...(isRegistration(context) && { registration: true }),
  };
}

/**
 * A "confirm" sign-in links nothing: it opens an Auth0 session for the already
 * attached identity so the application can then sign the person in silently.
 */
export interface MigrationConfirm {
  expectedIdentityId?: string;
  expectedEmail?: string;
  success: MigrationConfirmSuccess;
}

export interface MigrationLinkState {
  migrationLink: true;
  returnTo: string;
  confirm?: MigrationConfirm;
}

export function isMigrationLinkState(
  state: unknown
): state is MigrationLinkState {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as MigrationLinkState).migrationLink === true &&
    typeof (state as MigrationLinkState).returnTo === "string"
  );
}

export function isConfirmState(
  state: MigrationLinkState
): state is MigrationLinkState & { confirm: MigrationConfirm } {
  return typeof state.confirm === "object" && state.confirm !== null;
}
